import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Button, Card, CardContent, InputField, SubmitButton } from '@saas-kit/ui';

import type { CreateDisplayInput } from './apiDisplays';
import { DisplayLayoutFields } from './DisplayLayoutFields';
import {
  buildDisplayLayout,
  EMPTY_DISPLAY_FORM,
  hydrateDisplayForm,
  type DisplayFormState,
} from './displayFormState';
import type { QmsCounter, QmsCounterGroup, QmsDisplay } from './types';

/**
 * Create-or-edit form for a display screen. When `editing` is set the form
 * hydrates from that display and submits a PATCH; otherwise it creates.
 */
export function DisplayForm({
  groups,
  counters,
  editing,
  busy,
  onCreate,
  onUpdate,
  onCancelEdit,
}: {
  groups: QmsCounterGroup[];
  counters: QmsCounter[];
  editing: QmsDisplay | null;
  busy: boolean;
  onCreate: (input: CreateDisplayInput) => Promise<unknown>;
  onUpdate: (id: string, input: Record<string, unknown>) => Promise<unknown>;
  onCancelEdit: () => void;
}): JSX.Element {
  const { t } = useTranslation('qms');
  const [form, setForm] = useState<DisplayFormState>(EMPTY_DISPLAY_FORM);

  useEffect(() => {
    setForm(editing ? hydrateDisplayForm(editing) : EMPTY_DISPLAY_FORM);
  }, [editing]);

  const set = (patch: Partial<DisplayFormState>): void =>
    setForm((current) => ({ ...current, ...patch }));

  const targetMissing =
    (form.kind === 'group' && !form.group_id) ||
    (form.kind === 'counter' && !form.counter_id);

  const submit = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    if (!form.slug.trim() || !form.name.trim() || targetMissing) return;
    const layout = buildDisplayLayout(form);
    const slug = form.slug.trim().toLowerCase();
    const name = form.name.trim();
    if (editing) {
      await onUpdate(editing.id, { slug, name, layout });
    } else {
      await onCreate({ slug, name, layout, is_active: true });
      setForm(EMPTY_DISPLAY_FORM);
    }
  };

  return (
    <Card>
      <CardContent className="p-5">
        <form onSubmit={(e) => void submit(e)} className="space-y-4">
          <p className="text-sm font-medium text-foreground">
            {t(editing ? 'displays.form.edit_title' : 'displays.form.create_title')}
          </p>

          <div className="grid gap-3 sm:grid-cols-2">
            <InputField
              label={t('displays.slug')}
              hint={t('displays.slug_hint')}
              value={form.slug}
              onChange={(e) => set({ slug: e.target.value })}
              required
            />
            <InputField
              label={t('fields.name')}
              value={form.name}
              onChange={(e) => set({ name: e.target.value })}
              placeholder={t('placeholders.display_name')}
              required
            />
          </div>

          <DisplayLayoutFields
            form={form}
            groups={groups}
            counters={counters}
            onChange={set}
          />

          <div className="flex flex-wrap gap-2">
            <SubmitButton
              loading={busy}
              label={t(editing ? 'actions.save' : 'actions.create')}
              loadingLabel={t(editing ? 'actions.save' : 'actions.create')}
            />
            {editing ? (
              <Button type="button" variant="ghost" onClick={onCancelEdit}>
                {t('confirm.cancel')}
              </Button>
            ) : null}
          </div>
        </form>
      </CardContent>
    </Card>
  );
}
